import { Component, StrictMode, type ReactNode } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import App from './App.tsx'

type BootGuardState = {
  hasError: boolean
  message: string
  stack: string
}

class BootGuard extends Component<{ children: ReactNode }, BootGuardState> {
  state: BootGuardState = { hasError: false, message: '', stack: '' }

  static getDerivedStateFromError(error: unknown): BootGuardState {
    return {
      hasError: true,
      message: error instanceof Error ? error.message : 'Unknown startup error',
      stack: error instanceof Error ? (error.stack || '') : '',
    }
  }

  componentDidCatch(error: unknown) {
    console.error('App boot failed:', error)
  }

  private resetLocalState = () => {
    try {
      const keys = Object.keys(localStorage)
      for (const key of keys) {
        if (key.startsWith('odp-') || key.startsWith('app-state-') || key.endsWith('-v1')) {
          localStorage.removeItem(key)
        }
      }
    } catch {
      // ignore storage failures
    }
    window.location.reload()
  }

  render() {
    if (!this.state.hasError) return this.props.children

    return (
      <main style={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', padding: 24, fontFamily: 'Inter, system-ui, Arial, sans-serif', background: '#f6f5f1', color: '#1f1f1f' }}>
        <section style={{ maxWidth: 720, width: '100%', background: '#fff', border: '1px solid #e4e1d7', borderRadius: 12, padding: 20 }}>
          <h1 style={{ marginTop: 0 }}>Dashboard failed to load</h1>
          <p style={{ marginBottom: 8 }}>The app hit a startup error instead of rendering. You can safely reset local cached state and reload.</p>
          <pre style={{ whiteSpace: 'pre-wrap', background: '#f8f8f8', borderRadius: 8, padding: 12, border: '1px solid #eee', marginBottom: 16 }}>{this.state.message || 'No error message available.'}</pre>
          {this.state.stack ? (
            <details style={{ marginBottom: 16 }}>
              <summary>Technical stack trace</summary>
              <pre style={{ whiteSpace: 'pre-wrap', background: '#f8f8f8', borderRadius: 8, padding: 12, border: '1px solid #eee' }}>{this.state.stack}</pre>
            </details>
          ) : null}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button onClick={this.resetLocalState}>Reset local cache + reload</button>
            <button onClick={() => window.location.reload()}>Reload only</button>
          </div>
        </section>
      </main>
    )
  }
}

document.documentElement.classList.add('preload-ui')
window.requestAnimationFrame(() => {
  window.requestAnimationFrame(() => {
    document.documentElement.classList.remove('preload-ui')
  })
})

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BootGuard>
      <App />
    </BootGuard>
  </StrictMode>,
)
